import { lusolve, matrix, Matrix } from 'mathjs';
import { decorationAreaIsVisible, getLeftDecorationAngle, getRightDecorationAngle } from './tubeWarpInput';
import { CylinderWarpOutput, MapPoint, createCylinderWarpOutput, createSize } from './CylinderWarpOutput';
import { pointAtAngle } from './Ellipse';
import {
    CylinderType,
    PerspectiveTransformType,
    PlacementTransformType,
    Point,
    Size,
    TubeTransformType,
} from 'src/types/transforms';
import {
    generatePerspectiveTransformWithPoints,
    generatePlacementTransform,
    generateTubeTransform,
} from '../transformGenerators';

const LEFT_REFERENCE_ANGLE_RADIANS = (2 * Math.PI) / 3;
const RIGHT_REFERENCE_ANGLE_RADIANS = (1 * Math.PI) / 3;

interface ComputedInputs {
    CylinderTopLeft: Point;
    CylinderTopRight: Point;
    CylinderBottomLeft: Point;
    CylinderBottomRight: Point;
    DecorationTopLeft: Point;
    DecorationBottomRight: Point;
    DocumentSize: Size;
    TubeSize: Size;
    DocumentLeftRatio: number;
    DocumentRightRatio: number;
}

export const processTubeWarp = (input: CylinderType): CylinderWarpOutput => {
    if (!decorationAreaIsVisible(input)) {
        return createCylinderWarpOutput();
    }

    return internalProcess(input);
};

const internalProcess = (input: CylinderType): CylinderWarpOutput => {
    const computedInputs = computeInputs(input);

    // Coordinates of the perspective rectangle in image coordinates
    const imgFeatures: number[][] = [
        [computedInputs.CylinderTopLeft.x, computedInputs.CylinderTopLeft.y],
        [computedInputs.CylinderTopRight.x, computedInputs.CylinderTopRight.y],
        [computedInputs.CylinderBottomLeft.x, computedInputs.CylinderBottomLeft.y],
        [computedInputs.CylinderBottomRight.x, computedInputs.CylinderBottomRight.y],
    ];

    const texFeatures: number[][] = [
        [0, 0],
        [1, 0],
        [0, 1],
        [1, 1],
    ];

    // Compute perspective warp
    const perspective = makePerspectiveWarp(imgFeatures, texFeatures);

    // Perspective-transformed top-left and bottom-right
    const pdtl = normalize(
        mul(perspective, [computedInputs.DecorationTopLeft.x, computedInputs.DecorationTopLeft.y, 1.0]),
    );
    const pdbr = normalize(
        mul(perspective, [computedInputs.DecorationBottomRight.x, computedInputs.DecorationBottomRight.y, 1.0]),
    );

    // Below assumes vertical orientation

    // Let's compute the top and bottom offsets according to circular arc
    // This is parameter p from -1 to 1 based on the left/right axis
    const arctl = 2.0 * pdtl[0] - 1.0;
    const arcbr = 2.0 * pdbr[0] - 1.0;

    // These are the shifts assuming a 0,0 to 1,0 TubeWarp (for lowshift & highshift)
    const shiftTop = pdtl[1] / Math.sqrt(1.0 - arctl * arctl);
    const shiftBottom = (pdbr[1] - 1.0) / Math.sqrt(1.0 - arcbr * arcbr);

    // Texture coordinates of left and right assuming cylinder is 0,1 edge-to-edge
    const texl = (0.5 + Math.asin(arctl) / (2.0 * Math.PI)) * 2.0 - 0.5;
    const texr = (0.5 + Math.asin(arcbr) / (2.0 * Math.PI)) * 2.0 - 0.5;

    // Invert it these are source coordinates now (for MapPoint source=XXXX)
    let srcl = (0.0 - texl) / (texr - texl);
    let srcr = (1.0 - texl) / (texr - texl);
    srcl =
        srcl * (computedInputs.DocumentRightRatio - computedInputs.DocumentLeftRatio) +
        computedInputs.DocumentLeftRatio;
    srcr =
        srcr * (computedInputs.DocumentRightRatio - computedInputs.DocumentLeftRatio) +
        computedInputs.DocumentLeftRatio;

    // Determine the size and positioning of the cylinder warp
    const topShiftAllowance = -Math.min(shiftTop, 0);
    const bottomShiftAllowance = Math.max(shiftBottom, 0);
    const placementWarpHeight = 1 + topShiftAllowance + bottomShiftAllowance;
    const topMapping = +Number(topShiftAllowance / placementWarpHeight).toFixed(14);
    // const topMapping = topShiftAllowance / placementWarpHeight;
    const bottomMapping = +Number((topShiftAllowance + 1) / placementWarpHeight).toFixed(14);
    // const bottomMapping = (topShiftAllowance + 1) / placementWarpHeight;

    const cylinderWarpOutput: CylinderWarpOutput = {
        ...createCylinderWarpOutput(),
        documentSize: computedInputs.DocumentSize,
    };

    // Generate the placement warp
    if (input.isDecorationRotated) {
        cylinderWarpOutput.placementWarp = {
            rotation: 90,
            size: computedInputs.DocumentSize,
            mapPoint: {
                sourceX: 0.5,
                sourceY: 0.5,
                targetX: computedInputs.DocumentSize.width / 2.0,
                targetY: computedInputs.DocumentSize.height / 2.0,
            },
        };
    }

    // Generate the perspective warp
    cylinderWarpOutput.perspectiveWarp = {
        mapPoints: [
            {
                sourceX: 0,
                sourceY: topMapping,
                targetX: computedInputs.CylinderTopLeft.x,
                targetY: computedInputs.CylinderTopLeft.y,
            },
            {
                sourceX: 1,
                sourceY: topMapping,
                targetX: computedInputs.CylinderTopRight.x,
                targetY: computedInputs.CylinderTopRight.y,
            },
            {
                sourceX: 0,
                sourceY: bottomMapping,
                targetX: computedInputs.CylinderBottomLeft.x,
                targetY: computedInputs.CylinderBottomLeft.y,
            },
            {
                sourceX: 1,
                sourceY: bottomMapping,
                targetX: computedInputs.CylinderBottomRight.x,
                targetY: computedInputs.CylinderBottomRight.y,
            },
        ],
    };

    // Convert arc to universal coordinates
    const uc = computeUniversalCoordinates(input.decorationExtentDegreesAlpha, input.decorationExtentDegreesBeta);

    // Generate the tube warp
    cylinderWarpOutput.tubeWarp = {
        size: {
            width: computedInputs.TubeSize.width,
            height: Math.round(placementWarpHeight * computedInputs.TubeSize.height),
        },
        orientation: input.isDecorationRotated ? 'h' : 'v',
        lowShift: Math.round(computedInputs.TubeSize.height * shiftTop),
        highShift: Math.round(computedInputs.TubeSize.height * shiftBottom),
        mapPoints: [
            {
                sourceX: uc[0],
                sourceY: 0,
                targetX: 0,
                targetY: topShiftAllowance * computedInputs.TubeSize.height,
            },
            {
                sourceX: uc[1],
                sourceY: 1,
                targetX: computedInputs.TubeSize.width,
                targetY: (topShiftAllowance + 1) * computedInputs.TubeSize.height,
            },
        ],
    };

    // Add wrapped panel if the area wraps
    if (input.decorationExtentDegreesAlpha + input.decorationExtentDegreesBeta > 540) {
        cylinderWarpOutput.tubeWarp.mapPoints.push(
            {
                sourceX: uc[2],
                sourceY: 0,
                targetX: 0,
                targetY: topShiftAllowance * computedInputs.TubeSize.height,
            },
            {
                sourceX: uc[3],
                sourceY: 1,
                targetX: computedInputs.TubeSize.width,
                targetY: (topShiftAllowance + 1) * computedInputs.TubeSize.height,
            },
        );
    }

    cylinderWarpOutput.decorationAreaPoints = generateDecorationAreaOutlinePoints(
        input,
        imgFeatures,
        texFeatures,
        shiftTop,
        shiftBottom,
        srcl,
        srcr,
    );

    return cylinderWarpOutput;
};

const computeUniversalCoordinates = (Alpha: number, Beta: number): number[] => {
    const uc: number[] = new Array(4);

    uc[0] = (180 - Alpha) / Beta;
    uc[1] = 180 / Beta + uc[0];

    // Wrap panel
    if (Alpha + Beta > 540) {
        const Alpha2 = Alpha - 360;
        const Beta2 = Beta;

        uc[2] = (180 - Alpha2) / Beta;
        uc[3] = 180 / Beta2 + uc[2];
    }

    return uc;
};

const computeInputs = (input: CylinderType): ComputedInputs => {
    const leftDecorationAngle = getLeftDecorationAngle(input);
    const rightDecorationAngle = getRightDecorationAngle(input);

    const computedInputs: ComputedInputs = {
        CylinderTopLeft: pointAtAngle(input.cylinderTop, Math.PI),
        CylinderTopRight: pointAtAngle(input.cylinderTop, 0),
        CylinderBottomLeft: pointAtAngle(input.cylinderBottom, Math.PI),
        CylinderBottomRight: pointAtAngle(input.cylinderBottom, 0),
        DecorationTopLeft: pointAtAngle(input.cylinderTop, LEFT_REFERENCE_ANGLE_RADIANS),
        DecorationBottomRight: pointAtAngle(input.cylinderBottom, RIGHT_REFERENCE_ANGLE_RADIANS),
        DocumentLeftRatio: (Math.PI - leftDecorationAngle) / (rightDecorationAngle - leftDecorationAngle),
        DocumentRightRatio: (rightDecorationAngle - Math.PI) / (rightDecorationAngle - leftDecorationAngle),
        DocumentSize: createSize(),
        TubeSize: createSize(),
    };

    const maxImageExtent = Math.max(input.imageDimensions.width, input.imageDimensions.height);
    const scale = Math.min(
        maxImageExtent / input.decorationAreaDimensions.width,
        maxImageExtent / input.decorationAreaDimensions.height,
    );

    const docWidth = Math.round(input.decorationAreaDimensions.width * scale);
    const docHeight = Math.round(input.decorationAreaDimensions.height * scale);

    computedInputs.DocumentSize = {
        width: !input.isDecorationRotated ? docWidth : docHeight,
        height: !input.isDecorationRotated ? docHeight : docWidth,
    };

    computedInputs.TubeSize = {
        width: computedInputs.DocumentSize.width,
        height: computedInputs.DocumentSize.height,
    };

    if (input.isDecorationRotated) {
        computedInputs.TubeSize.height = Math.floor(
            computedInputs.TubeSize.height * (computedInputs.DocumentRightRatio - computedInputs.DocumentLeftRatio),
        );
    } else {
        computedInputs.TubeSize.width = Math.floor(
            computedInputs.TubeSize.width * (computedInputs.DocumentRightRatio - computedInputs.DocumentLeftRatio),
        );
    }

    return computedInputs;
};

const makePerspectiveWarp = (imgFeatures: number[][], texFeatures: number[][]): number[][] => {
    const lhs: number[][] = [
        [
            imgFeatures[0][0],
            imgFeatures[0][1],
            1,
            0,
            0,
            0,
            -texFeatures[0][0] * imgFeatures[0][0],
            -texFeatures[0][0] * imgFeatures[0][1],
        ],
        [
            0,
            0,
            0,
            imgFeatures[0][0],
            imgFeatures[0][1],
            1,
            -texFeatures[0][1] * imgFeatures[0][0],
            -texFeatures[0][1] * imgFeatures[0][1],
        ],
        [
            imgFeatures[1][0],
            imgFeatures[1][1],
            1,
            0,
            0,
            0,
            -texFeatures[1][0] * imgFeatures[1][0],
            -texFeatures[1][0] * imgFeatures[1][1],
        ],
        [
            0,
            0,
            0,
            imgFeatures[1][0],
            imgFeatures[1][1],
            1,
            -texFeatures[1][1] * imgFeatures[1][0],
            -texFeatures[1][1] * imgFeatures[1][1],
        ],
        [
            imgFeatures[2][0],
            imgFeatures[2][1],
            1,
            0,
            0,
            0,
            -texFeatures[2][0] * imgFeatures[2][0],
            -texFeatures[2][0] * imgFeatures[2][1],
        ],
        [
            0,
            0,
            0,
            imgFeatures[2][0],
            imgFeatures[2][1],
            1,
            -texFeatures[2][1] * imgFeatures[2][0],
            -texFeatures[2][1] * imgFeatures[2][1],
        ],
        [
            imgFeatures[3][0],
            imgFeatures[3][1],
            1,
            0,
            0,
            0,
            -texFeatures[3][0] * imgFeatures[3][0],
            -texFeatures[3][0] * imgFeatures[3][1],
        ],
        [
            0,
            0,
            0,
            imgFeatures[3][0],
            imgFeatures[3][1],
            1,
            -texFeatures[3][1] * imgFeatures[3][0],
            -texFeatures[3][1] * imgFeatures[3][1],
        ],
    ];

    const rhs: number[][] = [
        [texFeatures[0][0]],
        [texFeatures[0][1]],
        [texFeatures[1][0]],
        [texFeatures[1][1]],
        [texFeatures[2][0]],
        [texFeatures[2][1]],
        [texFeatures[3][0]],
        [texFeatures[3][1]],
    ];

    // Use mathjs lusolve to solve the linear system
    const solution = lusolve(matrix(lhs), matrix(rhs)) as Matrix;
    const solutionArray = solution.toArray() as number[][];

    const resultMatrix: number[][] = [
        [solutionArray[0][0], solutionArray[1][0], solutionArray[2][0]],
        [solutionArray[3][0], solutionArray[4][0], solutionArray[5][0]],
        [solutionArray[6][0], solutionArray[7][0], 1.0],
    ];

    return resultMatrix;
};

const generateDecorationAreaOutlinePoints = (
    input: CylinderType,
    imgFeatures: number[][],
    texFeatures: number[][],
    shiftTop: number,
    shiftBottom: number,
    srcl: number,
    srcr: number,
): MapPoint[] => {
    if (input.numberOfDecorationAreaPoints !== undefined && input.numberOfDecorationAreaPoints > 0) {
        const numberOfPoints = input.numberOfDecorationAreaPoints;

        const texuv: number[][] = Array.from({ length: numberOfPoints * 2 }, () => [0, 0]);

        // Place points across top and bottom rows
        for (let i = 0; i < numberOfPoints; i++) {
            const z = i / (numberOfPoints - 1.0);
            texuv[i + 0][0] = (z - srcl) / (srcr - srcl);
            texuv[i + numberOfPoints][0] = (z - srcl) / (srcr - srcl);
            texuv[i + 0][1] = 0.0;
            texuv[i + numberOfPoints][1] = 1.0;
        }

        // Clamp to edges
        for (let i = 0; i < numberOfPoints * 2; i++) {
            if (texuv[i][0] < 0.0) texuv[i][0] = 0.0;
            if (texuv[i][0] > 1.0) texuv[i][0] = 1.0;
        }

        // Emit points
        const iperspective = makePerspectiveWarp(texFeatures, imgFeatures);
        const points: MapPoint[] = [];

        for (let i = 0; i < numberOfPoints * 2; i++) {
            const u = texuv[i][0];
            const v = texuv[i][1];

            const p = Math.sin(Math.PI * (u - 0.5));
            const x = (p + 1.0) / 2.0;
            const c = Math.sqrt(1.0 - p * p);
            const a = shiftTop * c;
            const b = shiftBottom * c + 1.0;

            const y = v * (b - a) + a;

            const ixy = normalize(mul(iperspective, [x, y, 1.0]));

            points.push({
                sourceX: u,
                sourceY: v,
                targetX: ixy[0] / input.imageDimensions.width,
                targetY: ixy[1] / input.imageDimensions.height,
            });
        }

        return points;
    }

    return [];
};

const normalize = (v: number[]): number[] => {
    const factor = v[v.length - 1];
    return v.map((d, i) => (i === v.length - 1 ? 1 : d / factor));
};

const mul = (a: number[][], b: number[]): number[] => {
    if (a[0].length !== b.length) {
        throw new Error('Matrix dimensions do not match for multiplication');
    }

    const result: number[] = new Array(a.length);

    for (let i = 0; i < a.length; i++) {
        result[i] = 0.0;

        for (let j = 0; j < a[0].length; j++) {
            result[i] += a[i][j] * b[j];
        }
    }

    return result;
};

export type CylinderResponse = {
    documentSize: { width: number; height: number };
    perspectiveWarp: PerspectiveTransformType;
    tubeWarp: TubeTransformType;
    placementWarp?: PlacementTransformType;
};

export function parseCylinderWarp(
    response: CylinderWarpSceneTube,
    _imageDimensionsWidth: number,
    _imageDimensionsHeight: number,
    _documentId: string,
    perspectiveId: string,
    cylinderId: string,
    placementId: string,
) {
    // Todo decorationAreaPoints are an empty array. Why?
    const { documentSize, perspectiveWarp, tubeWarp, placementWarp } = response;
    if (!documentSize || !perspectiveWarp || !tubeWarp) {
        return {};
    }

    const mapPoints = (point: MapPoint) => ({
        x: point.targetX,
        y: point.targetY,
        sourceX: point.sourceX,
        sourceY: point.sourceY,
    });

    // This is to organize the perspective mapPoints in a clockwise fashion.
    const [point00, point10, point01, point11] = perspectiveWarp.mapPoints.map(mapPoints);

    const result: CylinderResponse = {
        documentSize,
        perspectiveWarp: generatePerspectiveTransformWithPoints({
            points: [point00, point10, point01, point11],
            id: perspectiveId,
        }),
        tubeWarp: generateTubeTransform({
            width: tubeWarp.size.width,
            height: tubeWarp.size.height,
            lowShift: tubeWarp.lowShift,
            highShift: tubeWarp.highShift,
            points: tubeWarp.mapPoints.map(mapPoints),
            id: cylinderId,
        }),
    };

    // rotation of the document needs a placementWarp
    if (placementWarp) {
        result.placementWarp = generatePlacementTransform({
            width: placementWarp.size.width,
            height: placementWarp.size.height,
            rotate: placementWarp.rotation,
            x1: placementWarp.mapPoint.targetX,
            y1: placementWarp.mapPoint.targetY,
            id: placementId,
        });
    }

    return result;
}

export type CylinderWarpSceneTube = {
    documentSize: Size;
    perspectiveWarp: {
        mapPoints: MapPoint[];
    };
    tubeWarp: {
        size: Size;
        lowShift: number;
        highShift: number;
        mapPoints: MapPoint[];
    };
    placementWarp: {
        size: Size;
        rotation: number;
        mapPoint: MapPoint;
    };
};
